import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { TEST_IDS } from "@/constants/testIds";

const HERO_IMAGE = "/hero-friends.png";

const lineVariants = {
  hidden: { clipPath: "inset(100% 0 0 0)", opacity: 0, y: 30 },
  visible: (i) => ({
    clipPath: "inset(0% 0 0 0)",
    opacity: 1,
    y: 0,
    transition: {
      delay: 0.3 + i * 0.18,
      duration: 0.8,
      ease: [0.16, 1, 0.3, 1],
    },
  }),
};

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: 0.9 + i * 0.12,
      duration: 0.7,
      ease: [0.16, 1, 0.3, 1],
    },
  }),
};

export default function Hero() {
  const scrollToSection = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      data-testid={TEST_IDS.HERO.root}
      className="relative min-h-screen flex items-center pt-28 lg:pt-0 overflow-hidden"
    >
      {/* Grain overlay */}
      <div className="grain-overlay absolute inset-0 pointer-events-none" />

      <div className="max-w-7xl mx-auto px-6 lg:px-10 w-full">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          {/* Text side */}
          <div className="lg:col-span-6 xl:col-span-5 pt-10 lg:pt-0">
            {/* Headline with line-by-line reveal */}
            <div data-testid={TEST_IDS.HERO.headline}>
              <motion.h1
                className="font-serif text-5xl sm:text-6xl lg:text-7xl font-bold text-plum tracking-tight leading-[1.05]"
                initial="hidden"
                animate="visible"
              >
                <motion.span className="block overflow-hidden">
                  <motion.span className="block" variants={lineVariants} custom={0}>
                    You belong
                  </motion.span>
                </motion.span>
                <motion.span className="block overflow-hidden">
                  <motion.span className="block" variants={lineVariants} custom={1}>
                    here<span className="text-amber">.</span>
                  </motion.span>
                </motion.span>
              </motion.h1>
            </div>

            <motion.p
              data-testid={TEST_IDS.HERO.subtext}
              className="mt-8 text-base lg:text-lg text-plum-light leading-relaxed max-w-md"
              variants={fadeUp}
              initial="hidden"
              animate="visible"
              custom={0}
            >
              The stuff that's a pain to shop for in person — bought in peace,
              with zero side-eye. Chosen by people who get it.
            </motion.p>

            <motion.div
              className="mt-10 flex flex-wrap gap-4"
              variants={fadeUp}
              initial="hidden"
              animate="visible"
              custom={1}
            >
              <Button
                data-testid={TEST_IDS.HERO.shopCta}
                onClick={() => scrollToSection("wear")}
                className="rounded-full bg-plum text-white hover:bg-plum/90 px-8 py-6 font-sans font-semibold text-base transition-transform duration-300 hover:-translate-y-0.5 hover:shadow-lg"
              >
                Shop the club
              </Button>
              <Button
                data-testid={TEST_IDS.HERO.howItWorks}
                onClick={() => scrollToSection("how-it-works")}
                variant="outline"
                className="rounded-full border-plum/20 text-plum hover:bg-plum/5 px-8 py-6 font-sans font-semibold text-base transition-transform duration-300 hover:-translate-y-0.5"
              >
                How it works
              </Button>
            </motion.div>
          </div>

          {/* Image side */}
          <motion.div
            className="lg:col-span-6 xl:col-span-7 relative flex items-end justify-center"
            initial={{ opacity: 0, scale: 0.95, y: 30 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 1, ease: [0.16, 1, 0.3, 1] }}
          >
            <img
              src={HERO_IMAGE}
              alt="Friends celebrating together with shopping bags"
              className="w-full max-w-xl lg:max-w-2xl h-auto object-contain drop-shadow-2xl"
              loading="eager"
            />

            {/* Floating badge */}
            <motion.div
              className="absolute -bottom-4 -left-4 lg:bottom-8 lg:-left-8 bg-white rounded-2xl p-4 lg:p-5 shadow-xl"
              initial={{ opacity: 0, y: 20, rotate: -3 }}
              animate={{ opacity: 1, y: 0, rotate: -3 }}
              transition={{ delay: 1.2, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
            >
              <p className="text-xs font-semibold uppercase tracking-widest text-plum-light">
                Every item
              </p>
              <p className="text-sm font-semibold text-plum mt-1">
                Checked for you
              </p>
              <div className="w-8 h-1 bg-amber rounded-full mt-2" />
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
